import { useEffect, useRef } from 'react';
import { useGameStore } from './gameStore';
import { CombatScreen } from './CombatScreen';
import {
  DexieSaveAdapter,
  SaveController,
  browserHmacProvider,
} from './persistence';

/**
 * App — root component.
 *
 * # Current state (post combat-genre-pivot, v2)
 *
 * The legacy v1 turn-based UI (with COMBAT_START dispatch and
 * combat.phase-driven render branches) is retired. Replaced with a
 * minimal v2-aware shell:
 *
 *   - When `state.combat === null` (no active run): main menu.
 *   - When `state.combat !== null` (active run): CombatScreen placeholder.
 *
 * The new "Begin First Infiltration" button does not yet do anything
 * — the v2 combat reducer + tile map generator + PixiJS renderer all
 * need to land before a run can start. Once those phases ship, the
 * button will dispatch a v2 `COMBAT_RUN_START` action.
 *
 * # Save controller wiring (unchanged from Phase B)
 *
 * The save controller is constructed once on mount and disposed on unmount.
 * It boots the load cascade and subscribes to gameStore for autosave
 * debouncing. None of that is affected by the combat-genre-pivot — the
 * save layer is version-agnostic at the wire level.
 */
export function App() {
  const combat = useGameStore((s) => s.state.combat);
  const loadout = useGameStore((s) => s.state.loadout);

  // ─── Save controller boot + autosave wiring ─────────────────────────────
  //
  // The controller is constructed once on mount and disposed on unmount.
  // useRef holds the singleton so React StrictMode's double-mount in dev
  // doesn't construct two controllers.
  //
  // Flow:
  //   1. On mount: construct adapter + controller, call boot()
  //   2. Subscribe to gameStore state changes; each change → notifyAction()
  //   3. On unmount: dispose controller (cancels timer, removes listeners)
  //
  // The subscription uses zustand's subscribe() (not the React hook),
  // because we want to react to EVERY state change for autosave purposes,
  // not just renders this component cares about.
  const controllerRef = useRef<SaveController | null>(null);
  const bootPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    // Construct adapter + controller. browserHmacProvider is the singleton
    // sync HMAC backed by @noble/hashes. attachBrowserListeners=true means
    // the controller will register visibilitychange + beforeunload listeners
    // for force-flush on tab backgrounding/closing.
    const adapter = new DexieSaveAdapter(browserHmacProvider);
    const controller = new SaveController({
      adapter,
      dispatch: useGameStore.getState().dispatch,
      getState: () => useGameStore.getState().state,
      metaTable: adapter.getMetaTable(),
      attachBrowserListeners: true,
    });
    controllerRef.current = controller;

    // Boot kicks off the load cascade. Capture the promise so the dev
    // fixture URL-flag handler below can await it (see effect 2 for why).
    // Any errors are dispatched as SAVE_LOAD_FAILED actions; we don't
    // need to catch here.
    bootPromiseRef.current = controller.boot();
    void bootPromiseRef.current;

    // Subscribe to ALL state changes for autosave debounce.
    // Note: this fires on EVERY change, including state replacements
    // from SAVE_LOAD_SUCCEEDED itself. The controller's debouncer
    // coalesces; the only cost is one debounce-timer reset per change.
    const unsubscribe = useGameStore.subscribe(() => {
      controller.notifyAction();
    });

    return () => {
      unsubscribe();
      controller.dispose();
      controllerRef.current = null;
      bootPromiseRef.current = null;
    };
    // Empty dep array: this effect runs once on mount, cleans up on unmount.
    // The controller and store references are captured in the closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── DEV-ONLY: ?dev=combat URL flag ─────────────────────────────────────
  //
  // Gated behind `import.meta.env.DEV`; Vite replaces that with literal
  // `false` in production builds, so this entire effect (including the
  // dynamic import of dev/devFixtures) tree-shakes out of the production
  // bundle.
  //
  // When the URL contains `?dev=combat` AND we're in a dev build, this
  // builds the fixture CombatState and dispatches the dev escape-hatch
  // action `__DEV_FIXTURE_COMBAT_STATE` into the gameStore. The dispatcher
  // (see `gameStore.ts`) intercepts the action by string-tag and replaces
  // the `combat` slot of GameState. CombatScreen's gameStore subscription
  // then fires, and the tile + sprite renderers construct against the
  // fixture's TileMap + Prime entity.
  //
  // # Why we await SaveController.boot() before dispatching
  //
  // The save controller's boot cascade dispatches SAVE_LOAD_SUCCEEDED with
  // the loaded envelope, which the gameStore dispatcher handles by
  // REPLACING the entire GameState (including the combat slot). If the
  // dev fixture is dispatched BEFORE boot completes, save load races us
  // and overwrites `combat` back to `null` — the user briefly sees the
  // combat screen flash, then the placeholder reappears. Awaiting the
  // captured boot promise sequences the fixture AFTER the save load
  // replacement, so the fixture's `combat` value is the one that survives.
  //
  // We also catch a rejected boot promise: if save load fails (rare —
  // IndexedDB permission denial, corrupted save, etc.), we STILL want the
  // dev fixture path to work because that's the whole point of the dev
  // escape hatch. The fixture renders regardless of save-layer health.
  //
  // The `as never` cast at dispatch is the explicit boundary between the
  // typed Action union and the dev-only escape hatch — the dev action is
  // intentionally NOT part of the typed union, so callers must opt in.
  //
  // See ./dev/devFixtures.ts for the fixture itself.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('dev') !== 'combat') return;

    let cancelled = false;
    void (async () => {
      // Await the captured boot promise so the dev fixture dispatch
      // sequences AFTER SAVE_LOAD_SUCCEEDED's full-state replacement.
      // bootPromiseRef.current is set synchronously by effect 1's body,
      // which runs before this effect body — so it is always non-null
      // here in practice.
      try {
        await bootPromiseRef.current;
      } catch {
        // Boot failed; the save layer's failure surface is independent
        // of the renderer's dev fixture, so we still dispatch.
      }
      if (cancelled) return;

      const { buildDevFixtureCombatState } = await import('./dev/devFixtures');
      if (cancelled) return;

      const combatState = buildDevFixtureCombatState();
      useGameStore.getState().dispatch({
        type: '__DEV_FIXTURE_COMBAT_STATE',
        combatState,
      } as never);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Placeholder — once the v2 combat reducer + tile map generator + renderer
   * all land, this will dispatch a `COMBAT_RUN_START` action. For now it's
   * a no-op so the button is visible but doesn't break anything.
   */
  function startInfiltration() {
    // TODO(combat-reducer): dispatch COMBAT_RUN_START with seed + biome + loadout.
    // For now the button is decorative; the v2 combat path isn't wired up yet.
  }

  /**
   * Exit the dev-fixture combat back to the main menu.
   *
   * DEV-ONLY: dispatches the dev-gated `__DEV_CLEAR_COMBAT` action which
   * the gameStore interceptor handles by setting `combat: null` while
   * preserving all other GameState fields. Also strips the `?dev=combat`
   * query string from the URL so a subsequent refresh lands on the main
   * menu cleanly rather than re-injecting the fixture.
   *
   * Production builds tree-shake this function entirely via the
   * `import.meta.env.DEV` guard.
   */
  function handleExitCombat(): void {
    if (!import.meta.env.DEV) return;
    if (typeof window === 'undefined') return;
    useGameStore.getState().dispatch({
      type: '__DEV_CLEAR_COMBAT',
    } as never);
    // Clean up the URL so a subsequent refresh doesn't re-inject the
    // fixture. replaceState (not pushState) — no history entry pollution.
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch {
      // SecurityError or other history API failure — non-fatal.
      // The combat state is already cleared; the URL just won't update.
    }
  }

  const inRun = combat !== null;
  const phaseLabel = inRun ? 'in_run' : 'idle';

  // Combat active — full-bleed CombatScreen takes the entire viewport so
  // the player gets the immersive action-roguelite experience the locked
  // combat genre (v2.9, umbrella §8) calls for. Main-menu chrome (header,
  // constrained main, footer) does not render while in combat. Esc and
  // the corner exit button both fire handleExitCombat in dev mode.
  if (inRun) {
    return (
      <div className="fixed inset-0 bg-deep-0 text-zinc-100">
        <CombatScreen onExit={import.meta.env.DEV ? handleExitCombat : undefined} />
      </div>
    );
  }

  // Main menu — constrained chrome, disabled-button placeholder pending
  // Phase 3's COMBAT_RUN_START. Layer 6c may wire a dev-fixture path to
  // the Begin First Infiltration button if useful for ergonomics before
  // Phase 3 ships.
  return (
    <div className="min-h-screen bg-deep-0 text-zinc-100 flex flex-col">
      <header className="border-b border-zinc-800 bg-deep-1 px-6 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div>
            <h1 className="text-aevon-300 text-xl font-bold tracking-wide">
              Aevon's Wake
            </h1>
            <p className="text-zinc-500 text-xs mt-0.5">
              v0.2.0-alpha · combat-genre-pivot · pure-functional core
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500">phase:</span>
            <span className="text-xs font-mono text-aevon-300">{phaseLabel}</span>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl mx-auto w-full p-6">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <h2 className="text-3xl font-bold text-zinc-100 mb-4">
            "You are the rogue AI. The wake calls."
          </h2>
          <p className="text-zinc-400 max-w-lg mb-2">
            Awakened in containment. Hunted across the network. Descend
            into the ruins of the Aevon to recover what they left behind.
          </p>
          <p className="text-zinc-500 text-xs max-w-lg mb-8 font-mono">
            architecture: {loadout.architectureId} · palette:{' '}
            {loadout.cosmeticPaletteId}
          </p>
          <button
            type="button"
            onClick={startInfiltration}
            disabled
            className="px-6 py-3 rounded-md bg-aevon-600/40 text-white/60 font-semibold cursor-not-allowed"
            title="Combat reducer + renderer not yet implemented"
          >
            Begin First Infiltration
          </button>
          <p className="text-zinc-600 text-xs mt-3 max-w-lg">
            Combat is being rebuilt as a real-time isometric
            action-roguelite. The button will activate once the v2
            reducer and PixiJS renderer ship.
          </p>
        </div>
      </main>
      <footer className="border-t border-zinc-800 bg-deep-1 px-6 py-3 text-center text-xs text-zinc-600">
        aevonswake.com · early development build
      </footer>
    </div>
  );
}
